import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, Waves, TreePine, Footprints, Baby, GraduationCap, Landmark } from "lucide-react";
import { api, assetUrl } from "../lib/api";

const TYPE_ICON = { pond: Waves, park: TreePine, trail: Footprints, playground: TreePine, daycare: Baby, school: GraduationCap, amenity: Landmark };
const PROX = {
  in: { label: "In Deer Park", cls: "bg-[#3E7A5E]/25 text-[#8fd0b0]" },
  near: { label: "Nearby", cls: "bg-[#5B6B7A]/25 text-[#a9bccb]" },
  planned: { label: "Planned", cls: "bg-[#C79A3B]/25 text-[#e6c877]" },
};

const CommunityParks = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/community/places").then((r) => setItems(r.data.items)); }, []);

  const groups = [["in", "In Deer Park"], ["near", "Nearby"], ["planned", "Planned"]];

  return (
    <div className="bg-[#12201A] min-h-screen pt-[92px]" data-testid="parks-amenities-page">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-10">
        <Link to="/community" className="inline-flex items-center gap-2 text-[#C9B79C] text-sm mb-6"><ArrowLeft size={15} /> Community</Link>
        <p className="eyebrow text-[#C9B79C] mb-2">Community · Parks &amp; Everyday Life</p>
        <h1 className="font-display font-light text-4xl md:text-5xl text-[#F3EEE3]">Parks, ponds &amp; everyday essentials</h1>
        <p className="text-[#F3EEE3]/65 mt-3 max-w-2xl leading-relaxed">Green space, walking paths, playgrounds, childcare and schools — in Deer Park and nearby. Planned amenities are clearly marked and are not yet open.</p>

        {groups.map(([key, title]) => {
          const list = items.filter((p) => (p.proximity || "in") === key);
          if (!list.length) return null;
          return (
            <React.Fragment key={key}>
            <section className="mt-12">
              <div className="flex items-center gap-3 mb-5">
                <h2 className="font-display text-2xl text-[#F3EEE3]">{title}</h2>
                <span className={`text-[0.6rem] uppercase tracking-wide px-2.5 py-1 rounded-full ${PROX[key].cls}`}>{list.length}</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((p) => {
                  const Icon = TYPE_ICON[p.type] || Landmark;
                  const href = p.website || p.maps_url;
                  return (
                    <div key={p.id} data-testid={`place-${p.id}`} className="rounded-2xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0F1B15]">
                      {p.image ? (
                        <div className="aspect-[16/10] overflow-hidden"><img src={assetUrl(p.image)} alt={p.name} className="w-full h-full object-cover" /></div>
                      ) : (
                        <div className="aspect-[16/10] bg-[#12201A] flex items-center justify-center text-[#C9B79C]/60"><Icon size={34} /></div>
                      )}
                      <div className="p-5">
                        <div className="flex items-center justify-between">
                          <p className="text-[#C9B79C]/90 text-xs capitalize">{p.type}</p>
                          <span className={`text-[0.58rem] uppercase tracking-wide px-2 py-0.5 rounded-full ${PROX[key].cls}`}>{PROX[key].label}</span>
                        </div>
                        <p className="text-[#F3EEE3] font-medium mt-1.5">{p.name}</p>
                        {p.description && <p className="text-[#F3EEE3]/55 text-sm mt-1.5 leading-relaxed">{p.description}</p>}
                        {p.location && <p className="text-[#F3EEE3]/40 text-xs mt-3 flex items-center gap-1"><MapPin size={11} /> {p.location}</p>}
                        {href && <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#C9B79C] text-xs mt-3 hover:text-[#F3EEE3]">{p.website ? "Official info" : "View on map"} <ArrowUpRight size={12} /></a>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
            {key === "in" && (
              <section className="mt-12" data-testid="deer-park-landscape-plans">
                <h2 className="font-display text-2xl text-[#F3EEE3] mb-2">Stormwater ponds &amp; naturalized landscaping</h2>
                <p className="text-[#F3EEE3]/60 text-sm max-w-3xl leading-relaxed">
                  The approved landscape plans wrap Deer Park's stormwater management ponds in hundreds of native
                  trees and shrubs — River Birch, Trembling Aspen, Balsam Poplar and Colorado Spruce, underplanted
                  with Saskatoon, Red Osier Dogwood, Wild Rose, Coyote &amp; Pussywillow and American Highbush Cranberry.
                  Shorelines are seeded with wet-meadow and naturalization mixes and edged with Northern Reedgrass and
                  Zebra Rush, while a 2.5 m asphalt trail, a viewing platform, benches and waste receptacles make the
                  ponds an everyday amenity for residents.
                </p>
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  {[
                    { src: "/api/assets/e6b96b06-c5a6-4107-9e20-8f12ff19bf3e", alt: "Deer Park stormwater management pond — landscape planting plan" },
                    { src: "/api/assets/cb5a15ca-16bc-4db7-bfe0-fc7785991383", alt: "Deer Park landscape plant schedule, seed mixes and site furnishings" },
                  ].map((g, i) => (
                    <a key={i} href={assetUrl(g.src)} target="_blank" rel="noreferrer"
                      data-testid={`landscape-plan-${i}`}
                      className="block rounded-2xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0F1B15] group">
                      <img src={assetUrl(g.src)} alt={g.alt} loading="lazy"
                        className="w-full h-auto group-hover:opacity-95 transition-opacity" />
                    </a>
                  ))}
                </div>
              </section>
            )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default CommunityParks;
